/**
 * Product analytics for speak (PostHog), web and native. First-party only: the events
 * go to our own PostHog project, are never joined with third-party data and carry no
 * advertising identifier — which is why Apple does not count them as tracking (no
 * ATT, no `NSUserTrackingUsageDescription`, `NSPrivacyTracking` stays false). They are
 * declared as "Product Interaction" + "Coarse Location" (PostHog's GeoIP), both
 * Analytics, in App Privacy and `ios/App/App/PrivacyInfo.xcprivacy`; as "App
 * interactions" + "Approximate location" in Play's Data safety; and in the privacy
 * policy (`public/legal/privacidad.html`).
 *
 * NO CONSENT UI, by design: Club Arena was rejected twice under 5.1.2(i) for an
 * in-app analytics / cookie prompt (Apple reads any such prompt as tracking). Do not
 * add one; changing what is collected means updating those four declarations first.
 *
 * OPT-OUT SWITCH (not a prompt): «Estadísticas de uso» in Perfil, stored per account
 * (`users.analytics_opt_out`, served as `user.analyticsOptOut`). `AnalyticsBridge`
 * applies it with `analyticsSetOptOut()` on every session load, BEFORE identifying:
 * opted out → PostHog `opt_out_capturing()` (no event, no autocapture, no $pageview)
 * and `track` / `analyticsIdentify` / person properties become no-ops, including the
 * calls already queued for the SDK; back in → `opt_in_capturing()` without its
 * `$opt_in` event. PostHog's `reset()` clears its consent state, so `analyticsReset`
 * re-applies the last known choice: after an opted-out account signs out, the device
 * stays out until the next account says otherwise.
 *
 * The no-PII rule: nothing from a conversation (transcripts, replies, corrections),
 * no names, no emails, no free text the learner typed. The distinct id is the
 * account's internal UUID. It is enforced three ways:
 *   1. Explicit events go through `track()`, typed by the closed catalog
 *      `AnalyticsEvents` (snake_case, primitive values only).
 *   2. `sanitizeProps()` drops, at runtime, any property whose name reads as content
 *      or identity (`text`, `email`, `name`, `comment`…) and any string value that
 *      looks like an email or is longer than `MAX_STRING_LENGTH`.
 *   3. Autocapture runs with `mask_all_text` + `mask_all_element_attributes` (the UI
 *      shows learner transcripts) and `before_send` strips the query and fragment of
 *      every URL property (`?token=` of the email links, OAuth codes).
 * No session recording, no surveys, no campaign/ad click ids, and no remote scripts
 * (`disable_external_dependency_loading`): the app never runs code it did not ship.
 *
 * Projects: production builds → "Orborix Speak" production; staging builds and the
 * local dev server (key in the `orborix-speak` env of ecosystem.config.cjs) → the
 * staging project. The `environment` super property tells them apart: `local` in the
 * Vite dev server, else `VITE_SENTRY_ENVIRONMENT` (`staging` / `production`).
 *
 * OFF without `VITE_POSTHOG_KEY` + `VITE_POSTHOG_HOST` (tests, previews, a dev server
 * started outside PM2): nothing is queued or loaded. A missing key is a legitimate
 * "analytics off" for those builds, but NOT for a production one: vite.config.ts fails the build when
 * `VITE_SENTRY_ENVIRONMENT=production` (web prod image, native production build-envs)
 * and either value is empty; and any build that sets one of the two must set the
 * other, plus `VITE_APP_VERSION` and `VITE_SENTRY_ENVIRONMENT` (the `app_version` /
 * `environment` super properties).
 *
 * DEFERRED LOAD (same as lib/sentry.ts): `posthog-js` stays out of the entry's static
 * graph; it is imported once the page has loaded and gone idle. Calls made before
 * that are queued (FIFO, capped) and drained in order.
 *
 * Telemetry never affects the user: every call is wrapped and never throws.
 */
import type { CaptureResult, PostHog, PostHogConfig, Properties } from 'posthog-js';
import { getPlugin, isNative, nativePlatform } from './platform.js';

// ---------------------------------------------------------------------------
// Event catalog — the only events the app sends explicitly (plus autocapture and
// $pageview). Keep it small: one event per step of the product funnel.
// ---------------------------------------------------------------------------

/** What kind of voice session (from the conversation's start context). */
export type VoiceSessionMode =
  | 'free'
  | 'program'
  | 'scenario'
  | 'exam'
  | 'a0'
  | 'listen'
  | 'orbitas';

export interface AnalyticsEvents {
  /** OTP code verified or a native Google/Apple sign-in, or a web OAuth redirect landed. */
  sign_in_completed: { method: 'otp' | 'google' | 'apple'; new_user: boolean | null };
  /**
   * The server stored the AI data consent, ticked on the registration triage
   * (/onboarding/level, also in its consent-only mode). `surface` stays a
   * property so another place to give it can be told apart later.
   */
  ai_consent_accepted: { surface: 'onboarding' };
  /** The level step flipped `onboarded` server-side. */
  onboarding_completed: { path: 'triage' | 'placement_quiz'; cefr_level: string | null };
  voice_session_started: { mode: VoiceSessionMode; resumed: boolean };
  /** Counts and duration only — never what was said. */
  voice_session_ended: {
    mode: VoiceSessionMode;
    resumed: boolean;
    duration_s: number;
    turns: number;
  };
  /** First question of a theory chat thread (per sub-unit). */
  theory_chat_used: { unit_id: string; sub_unit_id: string; coach_lang: string };
  game_started: { game: string };
  listen_chapter_started: { book: string; chapter: string; edition: string; cefr: string };
  /** A voice session in a Program unit went live. */
  program_unit_started: { unit_id: string; resumed: boolean };
  /** The debrief says this session completed / mastered the unit. */
  program_unit_completed: { unit_id: string; kind: 'program' | 'can_do' };
  purchase_sheet_opened: { purchasable: boolean };
  /** The store confirmed the purchase (no price: the app does not know it reliably). */
  purchase_completed: { product_id: string };
  push_permission_result: { result: 'granted' | 'denied'; prompted: boolean };
  /** The reason only: the reported text and the comment never leave in analytics. */
  ai_content_reported: { reason: string; surface: string };
}

export type AnalyticsEventName = keyof AnalyticsEvents;

type PropValue = string | number | boolean | null;

// ---------------------------------------------------------------------------
// No-PII guard
// ---------------------------------------------------------------------------

/** A property whose name reads as content or identity never leaves. */
const FORBIDDEN_KEY_RE =
  /(^|_)(text|transcript|content|message|comment|email|e_mail|name|first_name|last_name|phone|said|reply|answer|question|prompt)($|_)/i;
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
/** Ids, slugs and enums fit; a sentence does not. */
export const MAX_STRING_LENGTH = 80;

/**
 * The properties of one explicit event, minus anything that could be PII or learner
 * content (see the header). Dropped keys are reported in the console in dev. Exported
 * for tests.
 */
export function sanitizeProps(props: Record<string, unknown>): Record<string, PropValue> {
  const out: Record<string, PropValue> = {};
  for (const [key, value] of Object.entries(props)) {
    const ok =
      !FORBIDDEN_KEY_RE.test(key) &&
      (value === null ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        (typeof value === 'string' && value.length <= MAX_STRING_LENGTH && !EMAIL_RE.test(value)));
    if (ok) out[key] = value;
    else if (import.meta.env.DEV)
      console.error(`[analytics] dropped property «${key}» (no-PII rule)`);
  }
  return out;
}

/** `https://x/a?token=…#y` → `https://x/a`. Leaves anything that is not a URL alone. */
function stripQuery(value: string): string {
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
}

const URL_PROP_RE = /(url|referrer|href)$/i;

function scrubUrlProps(record: Properties | undefined): void {
  if (!record) return;
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string' && URL_PROP_RE.test(key)) record[key] = stripQuery(value);
  }
}

/**
 * `before_send` of every event, autocaptured ones included: strips the query and the
 * fragment of every URL-like property (`$current_url`, `$referrer`, their `$set` /
 * `$set_once` copies). Exported for tests.
 */
export function scrubAnalyticsEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  scrubUrlProps(event.properties);
  scrubUrlProps(event.$set);
  scrubUrlProps(event.$set_once);
  return event;
}

// ---------------------------------------------------------------------------
// Queue (deferred SDK)
// ---------------------------------------------------------------------------

let ph: PostHog | null = null;
/** Off for this page (no key / host, or the SDK chunk failed): nothing is queued. */
let disabled = false;
let initRequested = false;
/**
 * The «Estadísticas de uso» switch of the signed-in account (see the header): `true`
 * = nothing is captured. Kept across `analyticsReset()` on purpose.
 */
let optedOut = false;

/**
 * `data` = sends something about the person (events, identify, person properties)
 * and is dropped from the queue when they opt out; `control` = SDK state
 * (super properties, reset, the opt-out itself) and always runs.
 */
type OpKind = 'data' | 'control';

const pending: Array<{ kind: OpKind; op: (p: PostHog) => void }> = [];
const PENDING_CAP = 100;

function run(op: (p: PostHog) => void, kind: OpKind = 'control'): void {
  if (ph) {
    try {
      op(ph);
    } catch {
      // Telemetry never affects the user.
    }
    return;
  }
  if (disabled) return;
  if (pending.length >= PENDING_CAP) pending.shift();
  pending.push({ kind, op });
}

/** Puts the SDK in line with `optedOut` (idempotent; no `$opt_in` event). */
function applyOptOut(
  p: Pick<PostHog, 'has_opted_out_capturing' | 'opt_out_capturing' | 'opt_in_capturing'>,
): void {
  if (optedOut) {
    if (!p.has_opted_out_capturing()) p.opt_out_capturing();
  } else if (p.has_opted_out_capturing()) {
    p.opt_in_capturing({ captureEventName: false });
  }
}

/**
 * Super properties of the INSTALL (platform, app version, native build, OTA bundle):
 * remembered here so they survive `reset()`, which wipes every super property.
 */
const deviceProps: Record<string, string> = {};

/** Adds install-level super properties (e.g. `ota_bundle` from lib/native/ota.ts). IDs only. */
export function analyticsRegisterDevice(props: Record<string, string>): void {
  const clean = sanitizeProps(props) as Record<string, string>;
  Object.assign(deviceProps, clean);
  run((p) => {
    p.register(clean);
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** One explicit product event. Queued until the SDK loads; a no-op when analytics is off. */
export function track<E extends AnalyticsEventName>(event: E, props: AnalyticsEvents[E]): void {
  if (optedOut) return;
  const clean = sanitizeProps(props);
  run((p) => {
    p.capture(event, clean);
  }, 'data');
}

/**
 * Ties the following events to the account. The internal UUID only — never the email
 * or the name, neither as the id nor as a person property.
 */
export function analyticsIdentify(userId: string): void {
  if (optedOut) return;
  run((p) => {
    p.identify(userId);
  }, 'data');
}

/** Person properties (the learner's CEFR level). Same no-PII filter as the events. */
export function analyticsSetPersonProperties(props: { cefr_level: string }): void {
  if (optedOut) return;
  const clean = sanitizeProps(props);
  run((p) => {
    p.setPersonProperties(clean);
  }, 'data');
}

/**
 * The account's «Estadísticas de uso» switch (`user.analyticsOptOut`): `true` stops
 * every capture — explicit events, autocapture and $pageview — and drops the ones
 * still queued for the SDK; `false` resumes. Call it BEFORE `analyticsIdentify`.
 */
export function analyticsSetOptOut(optOut: boolean): void {
  optedOut = optOut;
  if (optOut) {
    for (let i = pending.length - 1; i >= 0; i--) {
      if (pending[i]!.kind === 'data') pending.splice(i, 1);
    }
  }
  run(applyOptOut);
}

/**
 * Sign-out and account deletion: a new anonymous id, so nothing that happens next is
 * attributed to the previous account. The install-level super properties come back.
 */
export function analyticsReset(): void {
  run((p) => {
    p.reset();
    // `reset()` also clears PostHog's consent state: put the last choice back.
    applyOptOut(p);
    if (Object.keys(deviceProps).length > 0) p.register({ ...deviceProps });
  });
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/** Page `load`, then idle: analytics yields the bandwidth to the content. */
function whenLoadedAndIdle(cb: () => void): void {
  const schedule = (): void => {
    if (typeof requestIdleCallback === 'function')
      requestIdleCallback(() => cb(), { timeout: 4000 });
    else setTimeout(cb, 2000);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}

/** The SDK configuration (exported for tests). */
export function analyticsConfig(host: string): Partial<PostHogConfig> {
  return {
    api_host: host,
    // A person profile only for signed-in accounts (identify); anonymous visitors of
    // the login screen stay anonymous events.
    person_profiles: 'identified_only',
    // SPA: one $pageview per route change (pushState / replaceState).
    capture_pageview: 'history_change',
    capture_pageleave: 'if_capture_pageview',
    // Clicks and form interactions, with every text and attribute masked: the UI
    // renders learner transcripts and coach replies.
    autocapture: { dom_event_allowlist: ['click', 'change', 'submit'] },
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Ad click ids (gclid, fbclid…) masked out of URLs and never saved as properties.
    mask_personal_data_properties: true,
    save_campaign_params: false,
    // No session replay (owner decision 2026-10-22: off everywhere). Errors are Sentry's
    // job; no heatmaps, no web vitals.
    disable_session_recording: true,
    disable_capture_url_hashes: true,
    capture_exceptions: false,
    capture_performance: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    // No remote code and no in-app PostHog UI (surveys, tours, chat, experiments).
    disable_external_dependency_loading: true,
    disable_surveys: true,
    disable_product_tours: true,
    disable_conversations: true,
    disable_web_experiments: true,
    // The app's WebView has no use for a cookie; the web keeps PostHog's default.
    persistence: isNative ? 'localStorage' : 'localStorage+cookie',
    before_send: scrubAnalyticsEvent,
  };
}

interface AppInfoPlugin {
  getInfo(): Promise<{ version?: string; build?: string }>;
}

/** Starts analytics for this page (main.tsx, once, before the first render). Never throws. */
export function initAnalytics(): void {
  if (initRequested || disabled) return;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const host = import.meta.env.VITE_POSTHOG_HOST as string | undefined;
  if (!key || !host) {
    disabled = true;
    pending.length = 0;
    return;
  }
  initRequested = true;
  // A build with the key always has both (vite.config.ts fails it otherwise); only the
  // dev server, which skips that check, may run without them.
  const appVersion = import.meta.env.VITE_APP_VERSION as string | undefined;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined;
  Object.assign(deviceProps, {
    platform: nativePlatform(),
    app_version: appVersion || (import.meta.env.DEV ? 'dev-server' : ''),
    environment: import.meta.env.DEV ? 'local' : (environment ?? ''),
  });
  if (isNative) void readNativeVersion();
  whenLoadedAndIdle(() => {
    void loadAndInit(key, host);
  });
}

/** Store version and build number of the binary (the JS bundle may be an OTA one). */
async function readNativeVersion(): Promise<void> {
  try {
    const info = await getPlugin<AppInfoPlugin>('App')?.getInfo();
    const props: Record<string, string> = {};
    if (info?.version) props.native_version = String(info.version);
    if (info?.build) props.native_build = String(info.build);
    if (Object.keys(props).length > 0) analyticsRegisterDevice(props);
  } catch {
    // leave them out
  }
}

async function loadAndInit(key: string, host: string): Promise<void> {
  try {
    const { default: posthog } = await import('posthog-js');
    posthog.init(key, {
      ...analyticsConfig(host),
      loaded: (instance) => {
        // Before the initial $pageview, which PostHog sends right after `loaded`.
        applyOptOut(instance);
        instance.register({ ...deviceProps });
      },
    });
    ph = posthog;
    console.info('[analytics] posthog initialized', { environment: deviceProps.environment });
    while (pending.length) {
      const { op } = pending.shift()!;
      try {
        op(posthog);
      } catch {
        // telemetry never throws
      }
    }
  } catch (error) {
    // The SDK chunk did not load (offline first launch, a stale chunk): this page goes
    // without analytics, and says so in the console.
    disabled = true;
    pending.length = 0;
    console.error('[analytics] could not load posthog-js — analytics off for this page', error);
  }
}

/** Test hook: back to the pristine module state. */
export function __resetAnalyticsForTests(): void {
  ph = null;
  disabled = false;
  initRequested = false;
  optedOut = false;
  pending.length = 0;
  for (const k of Object.keys(deviceProps)) delete deviceProps[k];
}
